/**
 * The appearance choice, and the one place that resolves it.
 *
 * TWO AXES, NOT ONE. GitHub's appearance page is the only survived prior art
 * that models this correctly: "sync with system" is not a third palette, it is
 * a statement about which of the palettes that exist should apply right now.
 * So the store carries two separate things and they must never be confused:
 *
 *   prefs.themeChoice   what the person asked for: system, night or day
 *   prefs.theme         what is actually painted right now: night or day
 *
 * Every screen, the token layer and the demo dock keep reading prefs.theme,
 * which is why nothing else in the app had to change. This module is the only
 * writer of prefs.theme, and it derives it from the choice, the active brand
 * direction and the operating system.
 *
 * THE DEFAULT, for a visitor who has never touched Appearance, is Day, and it
 * is held as an explicit choice in DEFAULT_THEME_CHOICE beside the rest of the
 * defaults in lib/mock/store.ts rather than as a special case inside
 * resolution. A default on the choice axis is one a person can see selected in
 * the picker and move. A default hidden in the resolver would be a rule that
 * changes what the screen does with nothing on screen to point at, and it would
 * cost 'system' its meaning: an option that answered Day to an operating system
 * asking for dark is not syncing with anything. An absent themeChoice, which is
 * what a store persisted before Appearance shipped carries, reads as that same
 * default here, because the bootstrap in index.html reads a missing mirror key
 * the same way, and the two have to agree or the first frame and the bundle
 * paint different themes.
 *
 * THE HONESTY RULE. Cleanroom has no day palette. A picker that offered Day
 * inside Cleanroom would be advertising a screen that does not exist, and a
 * "sync with system" that always answered Night would be worse, because it
 * would look like it was listening. So resolution clamps to Night for a
 * direction that ships one theme, and the picker renders one tile and says so
 * in a sentence. The stored choice is kept, not overwritten, so flipping the
 * direction back restores what the person actually picked.
 *
 * THE MIRROR. localStorage carries a plain copy of the choice and the
 * direction, written here and read by the inline script in index.html before
 * the bundle loads. See the comment on that script for why the zustand blob is
 * not what it reads.
 */

import { useCallback, useSyncExternalStore } from 'react';

import { DEFAULT_THEME_CHOICE, useDemoStore } from '../mock/store';
import type { Direction, Theme } from '../types';

/** What the person asked for. Distinct from Theme, which is what is painted. */
export type ThemeChoice = 'system' | 'night' | 'day';

/** Read before first paint by the bootstrap in index.html. Keep in step. */
export const THEME_MIRROR_KEY = 'sv-cloud-theme';
export const DIRECTION_MIRROR_KEY = 'sv-cloud-direction';

const DARK_QUERY = '(prefers-color-scheme: dark)';
const STILL_QUERY = '(prefers-reduced-motion: reduce)';

/** True when the direction has a day palette to switch to at all. */
export function directionShipsDay(direction: Direction): boolean {
  return direction === 'instrument';
}

/** The single resolution rule. The direction clamp comes first, deliberately. */
export function resolveTheme(
  choice: ThemeChoice,
  direction: Direction,
  prefersDark: boolean,
): Theme {
  if (!directionShipsDay(direction)) return 'night';
  if (choice === 'night' || choice === 'day') return choice;
  return prefersDark ? 'night' : 'day';
}

/* ----------------------------------------------------------- media state */

function mediaHook(query: string, serverValue: boolean) {
  const subscribe = (notify: () => void) => {
    const mq = window.matchMedia(query);
    mq.addEventListener('change', notify);
    return () => mq.removeEventListener('change', notify);
  };
  const read = () => window.matchMedia(query).matches;
  return () => useSyncExternalStore(subscribe, read, () => serverValue);
}

/** Live, so a person changing the OS setting sees this screen answer. */
export const usePrefersDark = mediaHook(DARK_QUERY, true);

/** Read only on the settings screen. The product never overrides it. */
export const usePrefersStill = mediaHook(STILL_QUERY, false);

/* --------------------------------------------------------- the controller */

let controllerStarted = false;

/**
 * Starts the one loop that keeps the choice, the store and the html element
 * agreeing. Called from main.tsx before the first render, so it runs outside
 * React: it has to reconcile a rehydrated store before anything paints, and it
 * has to keep working when the demo dock writes prefs.theme directly.
 *
 * The dock reconciliation is the interesting half. The dock sets prefs.theme
 * with no idea this module exists. When a write arrives that this module did
 * not make, it is treated as an explicit pick and promoted into themeChoice,
 * so the dock and the Appearance section can never disagree and neither one
 * needs to know about the other.
 */
export function startThemeController(): void {
  if (controllerStarted) return;
  controllerStarted = true;

  const dark = window.matchMedia(DARK_QUERY);

  /** The last theme this module wrote. Anything else came from outside. */
  let lastWritten: Theme | null = null;
  /** The choice as of the last settled pass, so a write that moved BOTH the
   *  theme and the choice can be told apart from one that moved the theme
   *  alone. See the promotion guard in reconcile. */
  let lastChoice: ThemeChoice | null = null;
  let mirroredChoice: ThemeChoice | null = null;
  let mirroredDirection: Direction | null = null;

  function mirror(choice: ThemeChoice, direction: Direction) {
    if (choice === mirroredChoice && direction === mirroredDirection) return;
    mirroredChoice = choice;
    mirroredDirection = direction;
    try {
      localStorage.setItem(THEME_MIRROR_KEY, choice);
      localStorage.setItem(DIRECTION_MIRROR_KEY, direction);
    } catch {
      // Private browsing, or storage disabled. The app still works, the only
      // loss is that the next load paints the markup default for one frame.
    }
  }

  function apply(direction: Direction, theme: Theme) {
    const root = document.documentElement;
    if (root.getAttribute('data-direction') !== direction) {
      root.setAttribute('data-direction', direction);
    }
    if (root.getAttribute('data-theme') !== theme) {
      root.setAttribute('data-theme', theme);
    }
  }

  function reconcile() {
    const store = useDemoStore.getState();
    const { prefs } = store;
    const choice: ThemeChoice = prefs.themeChoice ?? DEFAULT_THEME_CHOICE;
    const resolved = resolveTheme(choice, prefs.direction, dark.matches);

    if (prefs.theme !== resolved) {
      // THE PROMOTION GUARD. Promote only when the theme moved on its own.
      // The demo dock writes prefs.theme and nothing else, so an explicit pick
      // beats a derived one and gets adopted here. But "Reset the demo"
      // restores the whole prefs object in one write, theme and themeChoice
      // together. Reading that as a dock pick would overwrite the very default
      // the reset just restored, and the shape that failure took was bad: the
      // account got silently pinned to an explicit Night, and Sync with system
      // became unreachable from the only control that is supposed to produce
      // it. A write that moved the choice too is already authoritative about
      // the choice, so it is never a theme pick.
      //
      // This test was never the whole fix on its own, because it only catches a
      // reset whose choice moved. It missed the person sitting on Sync with
      // system in front of a machine asking for a light screen, back when the
      // defaults were Night and 'system': the reset put Night into theme while
      // themeChoice went from system to system, so the choice matched on both
      // sides, the theme looked like it had moved by itself, and the pin
      // happened anyway. That case is closed at the source now. The defaults
      // resolve to each other under either operating system setting, so a reset
      // lands already settled and never reaches this branch at all. Both halves
      // stay: the invariant in lib/mock/store.ts keeps resets out of here, and
      // this test still tells a genuine dock pick from a wholesale prefs write.
      const themeMovedAlone = choice === lastChoice;
      if (lastWritten !== null && prefs.theme !== lastWritten && themeMovedAlone) {
        store.setPref('themeChoice', prefs.theme);
        lastWritten = prefs.theme;
        lastChoice = prefs.theme;
      } else {
        store.setPref('theme', resolved);
        lastWritten = resolved;
        lastChoice = choice;
      }
      // The write re-enters through the subscription below, which is where
      // the mirror and the html attributes get set for the settled value.
      return;
    }

    lastWritten = resolved;
    lastChoice = choice;
    mirror(choice, prefs.direction);
    apply(prefs.direction, resolved);
  }

  useDemoStore.subscribe(reconcile);
  dark.addEventListener('change', reconcile);
  reconcile();
}

/* ------------------------------------------------------------- the screen */

export interface Appearance {
  direction: Direction;
  /** What the person asked for. */
  choice: ThemeChoice;
  setChoice: (next: ThemeChoice) => void;
  /** What is painted right now. */
  resolved: Theme;
  prefersDark: boolean;
  /** False when the active direction has no day palette to offer. */
  shipsDay: boolean;
}

export function useAppearance(): Appearance {
  const direction = useDemoStore((s) => s.prefs.direction);
  const stored = useDemoStore((s) => s.prefs.themeChoice);
  const setPref = useDemoStore((s) => s.setPref);
  const prefersDark = usePrefersDark();

  const choice: ThemeChoice = stored ?? DEFAULT_THEME_CHOICE;

  const setChoice = useCallback(
    (next: ThemeChoice) => setPref('themeChoice', next),
    [setPref],
  );

  return {
    direction,
    choice,
    setChoice,
    resolved: resolveTheme(choice, direction, prefersDark),
    prefersDark,
    shipsDay: directionShipsDay(direction),
  };
}
